import { useMutation } from '@apollo/client'
import { useTheme } from '@emotion/react'
import { Box, Stack } from '@mui/material'
import {
    EppScoreInput,
    GetCandidateProfileForRecruiterQuery,
} from '__generated__/graphql'
import { Dispatch, FC, SetStateAction, useEffect, useState } from 'react'
import { UpdateCandidatePersonalityScores } from 'recruiter/graphql/candidateProfile.gql'
import useToast from 'shared/components/Toast/useToast'
import { getPxAsRem } from 'shared/utils'
import { EDUCATION_SECTIONS } from '.'
import CcatScores, { CcatErrorFormValues, CcatFormValues } from './CcatScores'
import EditButtonsContainer from './EditButtonsContainer'
import EducationSectionTitleContainer from './EducationSectionTitleContainer'
import EppScores, { EppErrorFormValues, EppFormValues } from './EppScores'

interface PersonalityScoresProps {
    edit: EDUCATION_SECTIONS | null
    setEdit: Dispatch<SetStateAction<EDUCATION_SECTIONS | null>>
    personalityScores:
        | NonNullable<
              GetCandidateProfileForRecruiterQuery['candidate']
          >['personalityScores']
        | null
    candidateId: string
    closeAfterSave: () => void
    setUnsavedChangesExist: Dispatch<SetStateAction<boolean>>
    setEditWithUnsavedChanges: (newEdit: EDUCATION_SECTIONS | null) => void
}

export interface PersonalityScoresFormValues {
    ccatValues: CcatFormValues
    eppValues: EppFormValues
}

export interface PersonalityScoresErrorFormValues {
    ccatErrors: CcatErrorFormValues
    eppErrors: EppErrorFormValues
}

const PersonalityScores: FC<PersonalityScoresProps> = ({
    edit,
    setEdit,
    personalityScores,
    candidateId,
    closeAfterSave,
    setUnsavedChangesExist,
    setEditWithUnsavedChanges,
}) => {
    const toast = useToast()
    const theme = useTheme()

    const [updateScores] = useMutation(UpdateCandidatePersonalityScores)

    const getInitialFormValues = () => {
        return {
            ccatValues: {
                rawScore: personalityScores?.ccatRawScore,
                percentile: personalityScores?.ccatPercentile,
            },
            eppValues: {
                categoryValues: new Map(
                    personalityScores?.eppScores?.map(
                        ({ eppCategoryId, score }) => [eppCategoryId, score]
                    )
                ),
            },
        }
    }

    const getInitialErrorValues = () => {
        return {
            ccatErrors: {
                rawScore: '',
                percentile: '',
            },
            eppErrors: {
                categoryErrors: new Map(
                    personalityScores?.eppScores?.map(({ eppCategoryId }) => [
                        eppCategoryId,
                        '',
                    ])
                ),
            },
        }
    }

    const resetForm = () => {
        setFormValues(getInitialFormValues())
        setErrorValues(getInitialErrorValues())
    }
    const [formValues, setFormValues] = useState<PersonalityScoresFormValues>(
        getInitialFormValues()
    )
    const [errorValues, setErrorValues] =
        useState<PersonalityScoresErrorFormValues>(getInitialErrorValues())

    const unsavedChangesExist = (() => {
        const ccatChanged =
            formValues.ccatValues.percentile !==
                personalityScores?.ccatPercentile ||
            formValues.ccatValues.rawScore !== personalityScores?.ccatRawScore

        const existingScoreMap = new Map(
            personalityScores?.eppScores?.map(({ eppCategoryId, score }) => [
                eppCategoryId,
                score,
            ])
        )
        let eppChanged = false
        formValues.eppValues.categoryValues.forEach((score, categoryId) => {
            if (score !== (existingScoreMap.get(categoryId) ?? null)) {
                eppChanged = true
            }
        })

        return eppChanged || ccatChanged
    })()

    const hasValidationErrors = (() => {
        if (
            errorValues.ccatErrors.rawScore ||
            errorValues.ccatErrors.percentile
        )
            return true
        for (const error of errorValues.eppErrors.categoryErrors.values()) {
            if (error) return true
        }
        return false
    })()

    const handleSubmit = async (e) => {
        e.preventDefault()
        try {
            const eppScores: EppScoreInput[] = Array.from(
                formValues.eppValues.categoryValues.entries()
            )
                .filter(([_, score]) => score != null)
                .map(([key, score]) => ({
                    eppCategoryId: key,
                    score,
                }))
            await updateScores({
                variables: {
                    candidateId,
                    eppScores,
                    ccatScores: {
                        ccatPercentile: formValues.ccatValues.percentile,
                        ccatRawScore: formValues.ccatValues.rawScore,
                    },
                },
            })
            toast({
                severity: 'success',
                title: 'Success',
                message: 'Personality Scores have been updated',
            })
            closeAfterSave()
        } catch (e) {
            toast({
                severity: 'error',
                title: 'Error',
                message: 'Failed to update Personality Scores',
            })
        }
    }

    useEffect(() => {
        if (
            unsavedChangesExist &&
            edit === EDUCATION_SECTIONS.PERSONALITY_SCORES
        ) {
            setUnsavedChangesExist(true)
        }
    }, [unsavedChangesExist, edit, setUnsavedChangesExist])

    return (
        <>
            <form onSubmit={handleSubmit}>
                <EducationSectionTitleContainer
                    setEditWithUnsavedChanges={setEditWithUnsavedChanges}
                    edit={edit}
                    sectionType={EDUCATION_SECTIONS.PERSONALITY_SCORES}
                    title={'Personality Test Scores'}
                />

                <Box sx={{ display: 'flex' }}>
                    <Stack
                        direction={'column'}
                        spacing={1}
                        sx={{
                            flexGrow: 1,
                            borderRight: `1px solid ${theme.palette.other.focus_dark}`,
                            marginRight: getPxAsRem(32),
                        }}
                    >
                        <EppScores
                            isEditing={
                                edit === EDUCATION_SECTIONS.PERSONALITY_SCORES
                            }
                            onScoreUpdate={(newValue) => {
                                setFormValues({
                                    ...formValues,
                                    eppValues: newValue,
                                })
                            }}
                            updateErrorValues={(newValue) => {
                                setErrorValues({
                                    ...errorValues,
                                    eppErrors: newValue,
                                })
                            }}
                            errors={errorValues.eppErrors}
                            formValues={formValues.eppValues}
                        />
                    </Stack>
                    <Stack
                        direction={'column'}
                        spacing={1}
                        sx={{
                            flexGrow: 1,
                        }}
                    >
                        <CcatScores
                            isEditing={
                                edit === EDUCATION_SECTIONS.PERSONALITY_SCORES
                            }
                            onScoreUpdate={(newValue) => {
                                setFormValues({
                                    ...formValues,
                                    ccatValues: newValue,
                                })
                            }}
                            updateErrorValues={(newValue) => {
                                setErrorValues({
                                    ...errorValues,
                                    ccatErrors: newValue,
                                })
                            }}
                            errors={errorValues.ccatErrors}
                            formValues={formValues.ccatValues}
                        />
                    </Stack>
                </Box>

                <EditButtonsContainer
                    onCancel={resetForm}
                    activeEditingSection={edit}
                    sectionType={EDUCATION_SECTIONS.PERSONALITY_SCORES}
                    setEdit={setEdit}
                    setUnsavedChangesExist={setUnsavedChangesExist}
                    unsavedChangesExist={unsavedChangesExist}
                    hasValidationErrors={hasValidationErrors}
                />
            </form>
        </>
    )
}

export default PersonalityScores
